import React, { useState, useEffect, useCallback } from 'react';
import { X, Trash2, Download, RefreshCw, Database, Cpu, Wifi, WifiOff, Play, FileText, Image as ImageIcon, ChevronDown, ChevronRight, AlertTriangle, CheckCircle, Loader2, Upload, Rocket, ScrollText, Square, Power, StopCircle, Zap, ExternalLink } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? `http://${window.location.hostname}:3002` : '');

export default function VLMFineTuningPanel({ onClose }) {
  const [activeTab, setActiveTab] = useState('samples'); // 'samples' | 'datasets' | 'remote'
  const [samples, setSamples] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedSamples, setSelectedSamples] = useState(new Set());
  const [expandedSample, setExpandedSample] = useState(null);

  // JSONL 생성 옵션
  const [jsonlFormat, setJsonlFormat] = useState('qwen3-vl');
  const [jsonlTemplate, setJsonlTemplate] = useState('');
  const [generating, setGenerating] = useState(false);

  // SSH 상태
  const [sshStatus, setSshStatus] = useState(null);
  const [sshCommand, setSshCommand] = useState('');
  const [sshOutput, setSshOutput] = useState('');
  const [sshRunning, setSshRunning] = useState(false);

  // 업로드 / 학습 상태
  const [uploading, setUploading] = useState(null); // null | datasetFileName
  const [training, setTraining] = useState(false);
  const [trainingLog, setTrainingLog] = useState('');
  const [trainingRunning, setTrainingRunning] = useState(false);
  const [logPolling, setLogPolling] = useState(null);

  // vLLM 서버 상태
  const [vllmStatus, setVllmStatus] = useState(null); // { running, model, pid, gpu, finetunedModelExists }
  const [vllmLoading, setVllmLoading] = useState(false);

  // 데이터 로드
  const loadSamples = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/vlm-finetune/samples`);
      const data = await res.json();
      if (data.success) setSamples(data.samples);
    } catch (e) { console.error('샘플 로드 실패:', e); }
    setLoading(false);
  }, []);

  const loadDatasets = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/vlm-finetune/datasets`);
      const data = await res.json();
      if (data.success) setDatasets(data.datasets);
    } catch (e) { console.error('데이터셋 로드 실패:', e); }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/vlm-finetune/stats`);
      const data = await res.json();
      if (data.success) setStats(data.stats);
    } catch (e) { console.error('통계 로드 실패:', e); }
  }, []);

  useEffect(() => {
    loadSamples();
    loadStats();
    loadDatasets();
  }, [loadSamples, loadStats, loadDatasets]);

  // 샘플 삭제
  const handleDeleteSample = async (sampleId) => {
    if (!window.confirm('이 샘플을 삭제하시겠습니까?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/vlm-finetune/sample/${sampleId}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        setSamples(prev => prev.filter(s => s.sampleId !== sampleId));
        setSelectedSamples(prev => { const n = new Set(prev); n.delete(sampleId); return n; });
        loadStats();
      }
    } catch (e) { alert('삭제 실패: ' + e.message); }
  };

  // 선택된 샘플 일괄 삭제
  const handleBatchDelete = async () => {
    if (selectedSamples.size === 0) return;
    if (!window.confirm(`${selectedSamples.size}개 샘플을 삭제하시겠습니까?`)) return;
    for (const id of selectedSamples) {
      try {
        await fetch(`${API_BASE_URL}/api/vlm-finetune/sample/${id}`, { method: 'DELETE' });
      } catch (e) { /* continue */ }
    }
    setSelectedSamples(new Set());
    loadSamples();
    loadStats();
  };

  // JSONL 생성
  const handleGenerateJsonl = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/vlm-finetune/generate-jsonl`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          format: jsonlFormat,
          templateName: jsonlTemplate || undefined
        })
      });
      const data = await res.json();
      if (data.success) {
        alert(`✅ JSONL 생성 완료!\n\n파일: ${data.fileName}\n샘플 수: ${data.sampleCount}개\n크기: ${data.fileSize}\n스킵: ${data.skipped}개`);
        loadDatasets();
        loadStats();
      } else {
        alert('❌ ' + (data.error || 'JSONL 생성 실패'));
      }
    } catch (e) { alert('JSONL 생성 오류: ' + e.message); }
    setGenerating(false);
  };

  // 데이터셋 삭제
  const handleDeleteDataset = async (fileName) => {
    if (!window.confirm(`${fileName} 파일을 삭제하시겠습니까?`)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/vlm-finetune/dataset/${encodeURIComponent(fileName)}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        setDatasets(prev => prev.filter(d => d.name !== fileName));
      }
    } catch (e) { alert('삭제 실패: ' + e.message); }
  };

  // 데이터셋 → 원격 서버 업로드
  const handleUploadToRemote = async (datasetFileName) => {
    if (!window.confirm(`"${datasetFileName}"을 원격 서버에 업로드하시겠습니까?\n(JSONL + 이미지 파일 전송)`)) return;
    setUploading(datasetFileName);
    try {
      const res = await fetch(`${API_BASE_URL}/api/vlm-finetune/upload-to-remote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ datasetFileName })
      });
      const data = await res.json();
      if (data.success) {
        alert(`✅ 업로드 완료!\n\nJSONL: train_data.jsonl\n이미지: ${data.images}/${data.totalImages}개\n경로: ${data.remoteDir}`);
      } else {
        alert('❌ 업로드 실패: ' + (data.error || '알 수 없는 오류'));
      }
    } catch (e) { alert('업로드 오류: ' + e.message); }
    setUploading(null);
  };

  // 학습 시작
  const handleStartTraining = async () => {
    if (!window.confirm('원격 서버에서 파인튜닝을 시작하시겠습니까?\n\n사전에 데이터셋이 업로드되어 있어야 합니다.')) return;
    setTraining(true);
    setSshOutput('🚀 학습 스크립트 생성 + 전송 중...\n');
    try {
      const res = await fetch(`${API_BASE_URL}/api/vlm-finetune/start-training`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await res.json();
      if (data.success) {
        setSshOutput(`✅ 학습 시작됨! PID: ${data.pid}\n\n자동으로 로그를 표시합니다...\n`);
        // 자동 로그 폴링 시작
        startLogPolling();
      } else {
        setSshOutput('❌ 학습 시작 실패: ' + (data.error || ''));
      }
    } catch (e) { setSshOutput('❌ 오류: ' + e.message); }
    setTraining(false);
  };

  // 학습 로그 조회
  const fetchTrainingLog = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/vlm-finetune/training-log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lines: 80 })
      });
      const data = await res.json();
      if (data.success) {
        const status = data.running
          ? `🟢 학습 진행 중 (PID: ${data.pid})`
          : '⚪ 학습 프로세스 없음 (종료됨 또는 미시작)';
        setSshOutput(`${status}\n${'─'.repeat(50)}\n${data.log}`);
        setTrainingRunning(data.running);
        return data.running;
      } else {
        setSshOutput('❌ 로그 조회 실패: ' + (data.error || ''));
      }
    } catch (e) {
      setSshOutput('❌ 로그 조회 오류: ' + e.message);
    }
    return false;
  };

  // 로그 자동 폴링
  const startLogPolling = () => {
    stopLogPolling();
    fetchTrainingLog();
    const id = setInterval(async () => {
      const stillRunning = await fetchTrainingLog();
      if (!stillRunning) {
        clearInterval(id);
        setLogPolling(null);
      }
    }, 5000);
    setLogPolling(id);
  };

  const stopLogPolling = () => {
    if (logPolling) { clearInterval(logPolling); setLogPolling(null); }
  };

  // vLLM 상태 확인
  const checkVllmStatus = useCallback(async () => {
    setVllmLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/vlm-finetune/vllm/status`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({})
      });
      const data = await res.json();
      if (data.success) setVllmStatus(data);
    } catch (e) { console.error('vLLM 상태 확인 실패:', e); }
    setVllmLoading(false);
  }, []);

  // vLLM 중지
  const handleVllmStop = async () => {
    if (!window.confirm('vLLM 서버를 중지하시겠습니까?\n\nGPU 메모리가 해제되어 학습에 사용할 수 있습니다.')) return;
    setVllmLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/vlm-finetune/vllm/stop`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({})
      });
      const data = await res.json();
      if (data.success && data.stopped) {
        setVllmStatus(prev => ({ ...prev, running: false, pid: null, model: null }));
        setSshOutput('🛑 vLLM 서버 중지됨 — GPU 메모리 해제 완료\n');
      }
    } catch (e) { alert('vLLM 중지 실패: ' + e.message); }
    setTimeout(checkVllmStatus, 2000);
  };

  // vLLM 시작 (모델 선택)
  const handleVllmStart = async (model) => {
    const label = model === 'fp8' ? 'FP8 (원본)' : '파인튜닝';
    if (!window.confirm(`vLLM 서버를 ${label} 모델로 시작하시겠습니까?`)) return;
    setVllmLoading(true);
    setSshOutput(`🚀 vLLM 시작 중... (${label} 모델)\n모델 로딩에 1-3분 소요됩니다.\n`);
    try {
      const res = await fetch(`${API_BASE_URL}/api/vlm-finetune/vllm/start`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ model })
      });
      const data = await res.json();
      if (data.success) {
        setSshOutput(prev => prev + `✅ vLLM 시작 명령 전송됨 (PID: ${data.pid})\n모델 로딩 완료까지 대기 중...\n`);
        // 30초 후 상태 재확인 (모델 로딩 시간)
        setTimeout(checkVllmStatus, 30000);
      }
    } catch (e) { alert('vLLM 시작 실패: ' + e.message); }
    setVllmLoading(false);
  };

  // SSH 연결 테스트
  const handleTestSSH = async () => {
    setSshStatus('connecting');
    try {
      const res = await fetch(`${API_BASE_URL}/api/vlm-finetune/ssh/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await res.json();
      if (data.success) {
        setSshStatus(data);
        checkVllmStatus();
      } else {
        setSshStatus({ connected: false, error: data.error });
      }
    } catch (e) {
      setSshStatus({ connected: false, error: e.message });
    }
  };

  // SSH 명령 실행
  const handleRunSSH = async (cmd) => {
    const command = cmd || sshCommand;
    if (!command.trim()) return;
    setSshRunning(true);
    setSshOutput('실행 중...\n');
    try {
      const res = await fetch(`${API_BASE_URL}/api/vlm-finetune/ssh/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ command })
      });
      const data = await res.json();
      if (data.success) {
        setSshOutput(`[Exit: ${data.exitCode}]\n${data.stdout}${data.stderr ? '\n--- stderr ---\n' + data.stderr : ''}`);
      } else {
        setSshOutput('❌ ' + data.error);
      }
    } catch (e) {
      setSshOutput('❌ 오류: ' + e.message);
    }
    setSshRunning(false);
  };

  // 전체 선택 토글
  const toggleSelectAll = () => {
    if (selectedSamples.size === samples.length) {
      setSelectedSamples(new Set());
    } else {
      setSelectedSamples(new Set(samples.map(s => s.sampleId)));
    }
  };

  // 템플릿 목록 (통계에서 추출)
  const templateNames = stats ? Object.keys(stats.templateCounts) : [];

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[200]" onClick={onClose}>
      <div className="bg-[#f8f9fa] rounded-2xl shadow-2xl w-[1100px] h-[92vh] flex flex-col overflow-hidden border border-[#dadce0]" onClick={e => e.stopPropagation()}>
        {/* 헤더 - Google AI Studio 스타일 */}
        <div className="flex items-center justify-between px-6 py-3.5 bg-white border-b border-[#dadce0] shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#e8f0fe] flex items-center justify-center">
              <Database className="w-4.5 h-4.5 text-[#1a73e8]" />
            </div>
            <div>
              <h2 className="text-[15px] font-semibold text-[#202124]">VLM 파인튜닝</h2>
              {stats && (
                <span className="text-[11px] text-[#5f6368]">
                  {stats.totalSamples}개 샘플 · {stats.withImage}개 이미지
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#f1f3f4] rounded-full transition-colors">
            <X className="w-5 h-5 text-[#5f6368]" />
          </button>
        </div>

        {/* 탭 - Google Material 스타일 */}
        <div className="flex bg-white border-b border-[#dadce0] px-6 shrink-0">
          {[
            { key: 'samples', label: '학습 샘플', icon: ImageIcon, count: stats?.totalSamples },
            { key: 'datasets', label: 'JSONL 데이터셋', icon: FileText, count: datasets.length },
            { key: 'remote', label: '원격 서버', icon: Cpu }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => {
                setActiveTab(tab.key);
                if (tab.key === 'datasets') loadDatasets();
              }}
              className={`flex items-center gap-2 px-5 py-3 text-[13px] font-medium border-b-[3px] transition-all ${
                activeTab === tab.key
                  ? 'border-[#1a73e8] text-[#1a73e8]'
                  : 'border-transparent text-[#5f6368] hover:text-[#202124] hover:bg-[#f8f9fa]'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count !== undefined && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                  activeTab === tab.key ? 'bg-[#e8f0fe] text-[#1a73e8]' : 'bg-[#f1f3f4] text-[#5f6368]'
                }`}>{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* 콘텐츠 */}
        <div className="flex-1 overflow-hidden">
          {/* ─── 학습 샘플 탭 ─── */}
          {activeTab === 'samples' && (
            <div className="h-full flex flex-col">
              {/* 툴바 */}
              <div className="flex items-center justify-between px-5 py-2.5 border-b border-[#e8eaed] bg-white shrink-0">
                <div className="flex items-center gap-2">
                  <button onClick={loadSamples} className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] text-[#5f6368] hover:bg-[#f1f3f4] rounded-lg transition font-medium" title="새로고침">
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> 새로고침
                  </button>
                  {selectedSamples.size > 0 && (
                    <button onClick={handleBatchDelete} className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] text-[#d93025] hover:bg-[#fce8e6] rounded-lg transition font-medium">
                      <Trash2 className="w-3.5 h-3.5" /> {selectedSamples.size}개 삭제
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {templateNames.length > 0 && templateNames.map(name => (
                    <span key={name} className="text-[11px] bg-[#e8f0fe] text-[#1967d2] px-2 py-0.5 rounded-full font-medium">
                      {name} ({stats.templateCounts[name]})
                    </span>
                  ))}
                </div>
              </div>

              {/* 샘플 목록 */}
              <div className="flex-1 overflow-auto">
                {samples.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-[#5f6368]">
                    <Database className="w-14 h-14 mb-3 text-[#dadce0]" />
                    <p className="text-sm font-medium text-[#3c4043]">저장된 학습 샘플이 없습니다</p>
                    <p className="text-xs mt-1.5 text-[#80868b]">추출 결과 화면에서 "VLM 학습저장" 버튼을 사용하세요</p>
                  </div>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-gray-50 z-10">
                      <tr className="border-b border-gray-200">
                        <th className="px-3 py-2 text-left w-8">
                          <input type="checkbox" checked={selectedSamples.size === samples.length && samples.length > 0} onChange={toggleSelectAll} className="rounded" />
                        </th>
                        <th className="px-3 py-2 text-left w-8">IMG</th>
                        <th className="px-3 py-2 text-left">템플릿</th>
                        <th className="px-3 py-2 text-left">파일명</th>
                        <th className="px-3 py-2 text-center w-12">P</th>
                        <th className="px-3 py-2 text-center w-16">필드</th>
                        <th className="px-3 py-2 text-left w-36">저장일시</th>
                        <th className="px-3 py-2 text-center w-16">상태</th>
                        <th className="px-3 py-2 text-center w-12"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {samples.map(sample => (
                        <React.Fragment key={sample.sampleId}>
                          <tr
                            className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${selectedSamples.has(sample.sampleId) ? 'bg-indigo-50' : ''}`}
                            onClick={() => setExpandedSample(expandedSample === sample.sampleId ? null : sample.sampleId)}
                          >
                            <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={selectedSamples.has(sample.sampleId)}
                                onChange={() => {
                                  setSelectedSamples(prev => {
                                    const n = new Set(prev);
                                    n.has(sample.sampleId) ? n.delete(sample.sampleId) : n.add(sample.sampleId);
                                    return n;
                                  });
                                }}
                                className="rounded"
                              />
                            </td>
                            <td className="px-3 py-2">
                              {sample.imageSaved ? (
                                <div className="w-8 h-8 rounded border overflow-hidden bg-gray-100">
                                  <img
                                    src={`${API_BASE_URL}/vlm-finetune/${sample.sampleId}/${sample.imageFileName}`}
                                    alt=""
                                    className="w-full h-full object-cover"
                                    onError={e => { e.target.style.display = 'none'; }}
                                  />
                                </div>
                              ) : (
                                <div className="w-8 h-8 rounded border bg-gray-100 flex items-center justify-center">
                                  <AlertTriangle className="w-3 h-3 text-yellow-500" />
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 font-medium text-gray-900">{sample.templateName}</td>
                            <td className="px-3 py-2 text-gray-600 truncate max-w-[160px]" title={sample.fileName}>{sample.fileName || '-'}</td>
                            <td className="px-3 py-2 text-center text-gray-500">{sample.totalPages || sample.pageNumber || 1}</td>
                            <td className="px-3 py-2 text-center">
                              <span className="bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">{sample.fieldCount}</span>
                            </td>
                            <td className="px-3 py-2 text-gray-500">{new Date(sample.createdAt).toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                            <td className="px-3 py-2 text-center">
                              {sample.status === 'complete' ? (
                                <CheckCircle className="w-4 h-4 text-green-500 inline" />
                              ) : (
                                <AlertTriangle className="w-4 h-4 text-yellow-500 inline" />
                              )}
                            </td>
                            <td className="px-3 py-2 text-center" onClick={e => e.stopPropagation()}>
                              <button onClick={() => handleDeleteSample(sample.sampleId)} className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-500 transition">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                          {/* 확장 행: 상세 정보 */}
                          {expandedSample === sample.sampleId && (
                            <tr className="bg-gray-50">
                              <td colSpan={9} className="px-4 py-3">
                                <div className="flex gap-4">
                                  {sample.imageSaved && (
                                    <div className="flex gap-2 shrink-0">
                                      {(sample.imageFileNames || [sample.imageFileName]).map((imgFile, idx) => (
                                        <div key={idx} className="w-32 h-44 border rounded overflow-hidden bg-white">
                                          <img
                                            src={`${API_BASE_URL}/vlm-finetune/${sample.sampleId}/${imgFile}`}
                                            alt={`page ${idx + 1}`}
                                            className="w-full h-full object-contain"
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  <div className="flex-1 text-xs space-y-1">
                                    <div><b>샘플 ID:</b> <code className="bg-gray-100 px-1 rounded">{sample.sampleId}</code></div>
                                    <div><b>문서타입:</b> {sample.documentType || '-'}</div>
                                    <div><b>템플릿 ID:</b> {sample.templateId || '-'}</div>
                                    <div><b>필드 목록:</b> {(sample.fieldNames || []).join(', ') || '-'}</div>
                                    {sample.prompt && (
                                      <div><b>프롬프트:</b> <span className="text-gray-500">{sample.prompt.substring(0, 200)}...</span></div>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* ─── JSONL 데이터셋 탭 ─── */}
          {activeTab === 'datasets' && (
            <div className="h-full flex flex-col">
              {/* JSONL 생성 패널 */}
              <div className="px-5 py-3.5 border-b border-[#e8eaed] bg-white shrink-0">
                <div className="flex items-center gap-3">
                  <span className="text-[12px] font-semibold text-[#202124]">JSONL 생성</span>
                  <select value={jsonlFormat} onChange={e => setJsonlFormat(e.target.value)} className="text-[12px] border border-[#dadce0] rounded-lg px-2.5 py-1.5 bg-white text-[#3c4043] focus:border-[#1a73e8] focus:ring-1 focus:ring-[#1a73e8] outline-none">
                    <option value="qwen3-vl">Qwen3-VL</option>
                    <option value="gemma3">Gemma3 / 범용</option>
                  </select>
                  <select value={jsonlTemplate} onChange={e => setJsonlTemplate(e.target.value)} className="text-[12px] border border-[#dadce0] rounded-lg px-2.5 py-1.5 bg-white text-[#3c4043] focus:border-[#1a73e8] focus:ring-1 focus:ring-[#1a73e8] outline-none">
                    <option value="">전체 템플릿</option>
                    {templateNames.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                  <button
                    onClick={handleGenerateJsonl}
                    disabled={generating || !stats?.totalSamples}
                    className={`flex items-center gap-1.5 px-4 py-1.5 text-[12px] font-semibold rounded-full transition ${
                      generating ? 'bg-[#dadce0] text-[#80868b] cursor-wait' :
                      !stats?.totalSamples ? 'bg-[#f1f3f4] text-[#80868b] cursor-not-allowed' :
                      'bg-[#1a73e8] text-white hover:bg-[#1765cc] shadow-sm'
                    }`}
                  >
                    {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                    {generating ? '생성 중...' : 'JSONL 생성'}
                  </button>
                  <span className="text-[11px] text-[#5f6368]">
                    {stats?.withImage || 0}개 이미지 샘플 → JSONL
                  </span>
                </div>
              </div>

              {/* 데이터셋 목록 */}
              <div className="flex-1 overflow-auto">
                {datasets.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-[#5f6368]">
                    <FileText className="w-14 h-14 mb-3 text-[#dadce0]" />
                    <p className="text-sm font-medium text-[#3c4043]">생성된 JSONL 파일이 없습니다</p>
                    <p className="text-xs mt-1.5 text-[#80868b]">위의 "JSONL 생성" 버튼으로 학습 데이터셋을 만드세요</p>
                  </div>
                ) : (
                  <div className="p-4 space-y-2">
                    {datasets.map(ds => (
                      <div key={ds.name} className="bg-white rounded-xl border border-[#e8eaed] p-4 hover:border-[#1a73e8] hover:shadow-sm transition-all">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-10 h-10 rounded-lg bg-[#e8f0fe] flex items-center justify-center shrink-0">
                              <FileText className="w-5 h-5 text-[#1a73e8]" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-mono text-[12px] font-medium text-[#202124] truncate">{ds.name}</p>
                              <div className="flex items-center gap-3 mt-0.5">
                                <span className="text-[11px] text-[#5f6368]">{new Date(ds.createdAt).toLocaleString('ko-KR')}</span>
                                <span className="text-[11px] text-[#5f6368]">{ds.size}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <span className="bg-[#e6f4ea] text-[#137333] px-2.5 py-1 rounded-full text-[11px] font-semibold">
                              {ds.sampleCount}개 샘플
                            </span>
                            <button
                              onClick={() => handleUploadToRemote(ds.name)}
                              disabled={uploading === ds.name}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold bg-[#e8f0fe] text-[#1a73e8] hover:bg-[#d2e3fc] rounded-full transition disabled:opacity-50"
                              title="원격 서버에 업로드"
                            >
                              {uploading === ds.name ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                              {uploading === ds.name ? '전송 중...' : '서버 업로드'}
                            </button>
                            <button onClick={() => handleDeleteDataset(ds.name)} className="p-2 hover:bg-[#fce8e6] rounded-full text-[#80868b] hover:text-[#d93025] transition">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ─── 원격 서버 탭 ─── */}
          {activeTab === 'remote' && (
            <div className="h-full flex flex-col">
              {/* 서버 정보 + 학습 제어 - 컴팩트하게 한 줄 */}
              <div className="px-5 py-3 border-b border-[#e8eaed] bg-white shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#e8f5e9] flex items-center justify-center">
                      <Cpu className="w-4 h-4 text-[#137333]" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-semibold text-[#202124]">TensorDock L40S</span>
                        {sshStatus === 'connecting' ? (
                          <span className="flex items-center gap-1 text-[11px] text-[#e37400]"><Loader2 className="w-3 h-3 animate-spin" />연결 중</span>
                        ) : sshStatus?.connected ? (
                          <span className="flex items-center gap-1 text-[11px] text-[#137333]"><span className="w-1.5 h-1.5 bg-[#137333] rounded-full" />연결됨</span>
                        ) : sshStatus?.error ? (
                          <span className="flex items-center gap-1 text-[11px] text-[#d93025]"><WifiOff className="w-3 h-3" />실패</span>
                        ) : null}
                        {trainingRunning && (
                          <span className="flex items-center gap-1.5 text-[11px] text-[#137333] font-medium bg-[#e6f4ea] px-2 py-0.5 rounded-full">
                            <span className="w-2 h-2 bg-[#137333] rounded-full animate-pulse" />학습 진행 중
                          </span>
                        )}
                      </div>
                      <span className="text-[11px] text-[#5f6368]">8.17.147.162:10011 · L40S · 48 GiB</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleStartTraining}
                      disabled={training || sshRunning}
                      className="flex items-center gap-1.5 px-4 py-2 text-[12px] font-semibold bg-[#1a73e8] text-white rounded-full hover:bg-[#1765cc] transition disabled:opacity-50 shadow-sm"
                    >
                      {training ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" />}
                      {training ? '시작 중...' : '학습 시작'}
                    </button>
                    <button
                      onClick={fetchTrainingLog}
                      disabled={sshRunning}
                      className="flex items-center gap-1.5 px-3 py-2 text-[12px] font-medium text-[#1a73e8] bg-[#e8f0fe] hover:bg-[#d2e3fc] rounded-full transition disabled:opacity-50"
                    >
                      <ScrollText className="w-3.5 h-3.5" />
                      학습 로그
                    </button>
                    <a
                      href="https://wandb.ai/jmkim-loginnw-loginnetwork/docuai-vlm-finetune"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-2 text-[12px] font-medium text-[#f5a623] bg-[#fef7e0] hover:bg-[#feefc3] rounded-full transition"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />W&B
                    </a>
                    {logPolling ? (
                      <button onClick={stopLogPolling} className="flex items-center gap-1 px-3 py-2 text-[11px] font-medium bg-[#fef7e0] text-[#e37400] hover:bg-[#feefc3] rounded-full transition">
                        <Square className="w-3 h-3" fill="currentColor" />중지
                      </button>
                    ) : trainingRunning ? (
                      <button onClick={startLogPolling} className="flex items-center gap-1 px-3 py-2 text-[11px] font-medium bg-[#e6f4ea] text-[#137333] hover:bg-[#ceead6] rounded-full transition">
                        <RefreshCw className="w-3 h-3" />자동갱신
                      </button>
                    ) : null}
                    <button
                      onClick={handleTestSSH}
                      disabled={sshStatus === 'connecting'}
                      className="flex items-center gap-1 px-3 py-2 text-[11px] font-medium text-[#5f6368] bg-[#f1f3f4] hover:bg-[#e8eaed] rounded-full transition disabled:opacity-50"
                    >
                      <Wifi className="w-3 h-3" />SSH
                    </button>
                  </div>
                </div>

                {/* GPU 상태 */}
                {sshStatus?.gpu && (
                  <div className="mt-2.5 flex gap-2">
                    {sshStatus.gpu.map((g, i) => (
                      <div key={i} className="flex items-center gap-2.5 bg-[#f8f9fa] px-3 py-1.5 rounded-lg border border-[#e8eaed] text-[11px]">
                        <span className="font-semibold text-[#202124]">{g.name}</span>
                        <span className="text-[#5f6368]">VRAM: {g.memUsed}/{g.memTotal}</span>
                        <span className="text-[#5f6368]">GPU: {g.utilization}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* vLLM 서버 상태 + 제어 */}
                {sshStatus?.connected && (
                  <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                    {vllmLoading ? (
                      <span className="flex items-center gap-1.5 text-[11px] text-[#5f6368]"><Loader2 className="w-3 h-3 animate-spin" />vLLM 상태 확인 중...</span>
                    ) : vllmStatus?.running ? (
                      <>
                        <span className="flex items-center gap-1.5 text-[11px] font-medium text-[#137333] bg-[#e6f4ea] px-2.5 py-1 rounded-full">
                          <Zap className="w-3 h-3" />vLLM 실행 중
                        </span>
                        {vllmStatus.model && (
                          <span className="text-[11px] text-[#5f6368] bg-[#f1f3f4] px-2 py-1 rounded-full font-mono">{vllmStatus.model}</span>
                        )}
                        {vllmStatus.gpu && (
                          <span className="text-[11px] text-[#5f6368]">VRAM: {vllmStatus.gpu.memUsed}/{vllmStatus.gpu.memTotal}</span>
                        )}
                        <button onClick={handleVllmStop} disabled={vllmLoading} className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-[#d93025] bg-[#fce8e6] hover:bg-[#f5c6c2] rounded-full transition disabled:opacity-50">
                          <StopCircle className="w-3 h-3" />중지
                        </button>
                        <button onClick={checkVllmStatus} disabled={vllmLoading} className="p-1 text-[#5f6368] hover:bg-[#f1f3f4] rounded-full transition disabled:opacity-50">
                          <RefreshCw className="w-3 h-3" />
                        </button>
                      </>
                    ) : vllmStatus ? (
                      <>
                        <span className="flex items-center gap-1.5 text-[11px] font-medium text-[#d93025] bg-[#fce8e6] px-2.5 py-1 rounded-full">
                          <StopCircle className="w-3 h-3" />vLLM 중지됨
                        </span>
                        <button onClick={() => handleVllmStart('fp8')} disabled={vllmLoading} className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-white bg-[#1a73e8] hover:bg-[#1765cc] rounded-full transition disabled:opacity-50">
                          <Power className="w-3 h-3" />FP8 모델 시작
                        </button>
                        {vllmStatus.finetunedModelExists && (
                          <button onClick={() => handleVllmStart('finetuned')} disabled={vllmLoading} className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-white bg-[#137333] hover:bg-[#0d5c28] rounded-full transition disabled:opacity-50">
                            <Power className="w-3 h-3" />파인튜닝 모델 시작
                          </button>
                        )}
                        <button onClick={checkVllmStatus} disabled={vllmLoading} className="p-1 text-[#5f6368] hover:bg-[#f1f3f4] rounded-full transition disabled:opacity-50">
                          <RefreshCw className="w-3 h-3" />
                        </button>
                      </>
                    ) : (
                      <button onClick={checkVllmStatus} disabled={vllmLoading} className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-[#1a73e8] bg-[#e8f0fe] hover:bg-[#d2e3fc] rounded-full transition disabled:opacity-50">
                        <RefreshCw className="w-3 h-3" />vLLM 상태 확인
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* 빠른 명령 + 커맨드 입력 - 한 줄로 컴팩트 */}
              <div className="px-5 py-2 border-b border-[#e8eaed] bg-white shrink-0">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  {[
                    { label: 'GPU', cmd: 'nvidia-smi' },
                    { label: '디스크', cmd: 'df -h /home/user/workspace' },
                    { label: 'vLLM', cmd: 'ps aux | grep vllm | grep -v grep && curl -s http://localhost:8000/v1/models | python3 -m json.tool 2>/dev/null || echo "vLLM 서버 미실행"' },
                    { label: '모델', cmd: 'ls -lh /home/user/workspace/qwen3vl-32b-fp8/*.safetensors 2>/dev/null | wc -l && du -sh /home/user/workspace/qwen3vl-32b-fp8/ 2>/dev/null || echo "모델 없음"' },
                    { label: 'VRAM', cmd: 'nvidia-smi --query-gpu=name,memory.total,memory.free,memory.used,temperature.gpu,utilization.gpu --format=csv' },
                    { label: '프로세스', cmd: 'ps aux | grep -E "train|finetune|vllm" | grep -v grep' },
                    { label: 'LoRA 결과', cmd: 'ls -la /home/user/workspace/finetuning/lora_adapters/ 2>/dev/null && echo "---" && cat /home/user/workspace/finetuning/data/training.log 2>/dev/null | tail -20 || echo "(LoRA 출력 없음)"' },
                    { label: '학습 로그', cmd: 'tail -50 /home/user/workspace/finetuning/data/training.log 2>/dev/null || echo "(로그 없음)"' },
                    { label: '업로드 확인', cmd: 'ls -la /home/user/workspace/finetuning/data/ && echo "--- images ---" && ls /home/user/workspace/finetuning/data/images/ 2>/dev/null || echo "(no images)"' },
                  ].map(({ label, cmd }) => (
                    <button
                      key={cmd}
                      onClick={() => { setSshCommand(cmd); handleRunSSH(cmd); }}
                      disabled={sshRunning}
                      className="px-2.5 py-1 text-[11px] text-[#5f6368] bg-[#f1f3f4] hover:bg-[#e8eaed] rounded-full transition font-medium disabled:opacity-50"
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    value={sshCommand}
                    onChange={e => setSshCommand(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleRunSSH()}
                    placeholder="SSH 명령어 입력..."
                    className="flex-1 text-[12px] font-mono border border-[#dadce0] rounded-lg px-3 py-2 focus:border-[#1a73e8] focus:ring-1 focus:ring-[#1a73e8] outline-none bg-[#f8f9fa] text-[#202124]"
                  />
                  <button
                    onClick={() => handleRunSSH()}
                    disabled={sshRunning || !sshCommand.trim()}
                    className="flex items-center gap-1.5 px-4 py-2 text-[12px] font-semibold bg-[#202124] text-white rounded-lg hover:bg-[#3c4043] transition disabled:opacity-50"
                  >
                    {sshRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                    실행
                  </button>
                </div>
              </div>

              {/* 출력 - 최대한 넓게 */}
              <div className="flex-1 overflow-auto bg-[#1e1e1e] p-5" style={{ minHeight: '300px' }}>
                <pre className="text-[12px] font-mono text-[#d4d4d4] whitespace-pre-wrap leading-relaxed">
                  {sshOutput || (
                    <span className="text-[#6a9955]">
                      {'# TensorDock L40S 터미널 (L40S 48GB)\n# ─────────────────────────────────────────\n# 📁 모델: /home/user/workspace/qwen3vl-32b-fp8\n# 📁 학습 데이터: /home/user/workspace/finetuning/data/\n# 📁 LoRA 출력: /home/user/workspace/finetuning/lora_adapters/\n# 📁 학습 스크립트: /home/user/workspace/finetuning/\n#\n# 워크플로우:\n# 1. "서버 업로드" → JSONL + 이미지 전송\n# 2. "학습 시작" → QLoRA 파인튜닝 실행\n# 3. "학습 로그" → 진행 상황 모니터링\n# 4. 학습 완료 후 → LoRA 병합 → vLLM 재시작\n#\n# 명령어를 직접 입력하여 실행할 수도 있습니다'}
                    </span>
                  )}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
